import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Plus, Trash2, Sparkles, Loader2, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useState } from "react";
import { toast } from "sonner";
import AIPromptDialog from "../AIPromptDialog";
import WordCount from "../WordCount";

const SUMMARY_QUESTIONS = [
  { key: "role", label: "What's your current or most recent role?", placeholder: "e.g. Warehouse Lead" },
  { key: "years", label: "How many years have you done this kind of work?", placeholder: "e.g. 7 years" },
  { key: "strengths", label: "What are 2–3 things you're good at?", placeholder: "e.g. safety, training new hires, forklift operation" },
  { key: "target", label: "What kind of role do you want next? (optional)", placeholder: "e.g. shift supervisor" },
];

const BULLETS_QUESTIONS = [
  { key: "duty", label: "What did you do in this role?", placeholder: "e.g. loaded and unloaded trucks; operated forklift" },
  { key: "result", label: "Any numbers or results? (volume, accuracy, accidents, etc.)", placeholder: "e.g. 0 accidents in 18 months; 200 pallets/day" },
  { key: "tools", label: "Any tools, software, or methods you used?", placeholder: "e.g. forklift, pallet jack, SAP, OSHA training" },
  { key: "people", label: "Anyone you worked with or trained? (optional)", placeholder: "e.g. trained 6 new hires; led team of 4" },
];

function AiImproveButton({ context, onResult, small = false }) {
  // "Improve" only uses the user's own existing text. No questions needed.
  const [loading, setLoading] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={loading}
      className={`text-xs gap-1 border-blue-200 text-blue-700 bg-blue-50/50 hover:bg-blue-50 hover:text-blue-800 ${small ? "h-7 px-2" : "h-8"}`}
      onClick={async () => {
        if (!context || !context.trim()) {
          toast.error("Write something first, then the AI can improve it.");
          return;
        }
        setLoading(true);
        try {
          const { data } = await api.post("/ai/assist", { action: "improve", context });
          if (data.text && data.text.startsWith("NEED:")) {
            toast.error(data.text.replace(/^NEED:\s*/, "Need more info: "));
            return;
          }
          onResult(data.text);
          toast.success("Improved");
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
          setLoading(false);
        }
      }}
      data-testid="ai-improve-btn"
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {small ? "" : "Improve"}
    </Button>
  );
}

export function PersonalInfoStep({ resume, update }) {
  const pi = resume.personalInfo || {};
  const set = (k, v) => update({ ...resume, personalInfo: { ...pi, [k]: v } });
  const fields = [
    ["fullName", "Full name", "Jordan Rivera"],
    ["headline", "Job title / headline", "Senior Product Designer"],
    ["email", "Email", "jordan@example.com"],
    ["phone", "Phone", "+1 555 0100"],
    ["location", "Location", "San Francisco, CA"],
    ["website", "Website / Portfolio", "jordanrivera.com"],
    ["linkedin", "LinkedIn", "linkedin.com/in/jordan"],
  ];
  return (
    <div>
      <StepHeader title="Personal information" desc="Basic contact details to display at the top of your resume.">
        <span className="text-xs text-emerald-700 inline-flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
          <Lock className="w-3 h-3" /> Encrypted on save
        </span>
      </StepHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(([k, l, ph]) => (
          <div key={k}>
            <Label className="text-sm">{l}</Label>
            <Input
              value={pi[k] || ""}
              onChange={(e) => set(k, e.target.value)}
              placeholder={ph}
              className="mt-1.5"
              data-testid={`personal-${k}-input`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SummaryStep({ resume, update }) {
  const [aiOpen, setAiOpen] = useState(false);
  return (
    <div>
      <StepHeader title="Professional summary" desc="2–3 sentences about your experience, strengths, and what you're targeting next." />
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-sm">Summary</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1 border-blue-200 text-blue-700 bg-blue-50/50 hover:bg-blue-50 hover:text-blue-800"
            onClick={() => setAiOpen(true)}
            data-testid="ai-summary-open-btn"
          >
            <Sparkles className="w-3 h-3" /> Ask AI to write it
          </Button>
          {resume.summary && (
            <AiImproveButton context={resume.summary} onResult={(t) => update({ ...resume, summary: t })} />
          )}
        </div>
      </div>
      <Textarea
        rows={6}
        value={resume.summary || ""}
        onChange={(e) => update({ ...resume, summary: e.target.value })}
        placeholder="Senior product designer with 7+ years shipping consumer SaaS products…"
        data-testid="summary-textarea"
      />
      <WordCount text={resume.summary || ""} suggestedMax={60} />      <AIPromptDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        title="A few quick questions"
        description="Answer what you can. I'll only use what you write here — no made-up facts."
        questions={SUMMARY_QUESTIONS}
        action="summary"
        context={resume.summary || ""}
        onResult={(t) => update({ ...resume, summary: t })}
      />
    </div>
  );
}

export function ExperienceStep({ resume, update }) {
  const exp = resume.experience || [];
  const [aiJobIdx, setAiJobIdx] = useState(null);
  const set = (i, k, v) => {
    const copy = [...exp];
    copy[i] = { ...copy[i], [k]: v };
    update({ ...resume, experience: copy });
  };
  const setBullet = (i, j, v) => {
    const copy = [...exp];
    const bullets = [...(copy[i].bullets || [])];
    bullets[j] = v;
    copy[i] = { ...copy[i], bullets };
    update({ ...resume, experience: copy });
  };
  const addJob = () => update({ ...resume, experience: [...exp, { role: "", company: "", location: "", startDate: "", endDate: "", current: false, bullets: [""] }] });
  const removeJob = (i) => update({ ...resume, experience: exp.filter((_, idx) => idx !== i) });
  const addBullet = (i) => set(i, "bullets", [...(exp[i].bullets || []), ""]);
  const removeBullet = (i, j) => set(i, "bullets", (exp[i].bullets || []).filter((_, idx) => idx !== j));

  return (
    <div>
      <StepHeader title="Work experience" desc="List your most recent roles first. Use action verbs and quantify outcomes." />
      <div className="space-y-5">
        {exp.map((e, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-700">Job #{i + 1}</div>
              <Button variant="ghost" size="sm" onClick={() => removeJob(i)} className="text-red-600 hover:text-red-700 h-8" data-testid={`remove-job-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Role"><Input value={e.role || ""} onChange={(ev) => set(i, "role", ev.target.value)} placeholder="Senior Product Designer" data-testid={`exp-role-${i}`} /></Field>
              <Field label="Company"><Input value={e.company || ""} onChange={(ev) => set(i, "company", ev.target.value)} placeholder="Acme Inc" /></Field>
              <Field label="Location"><Input value={e.location || ""} onChange={(ev) => set(i, "location", ev.target.value)} placeholder="Remote" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start"><Input value={e.startDate || ""} onChange={(ev) => set(i, "startDate", ev.target.value)} placeholder="Jan 2022" /></Field>
                <Field label="End"><Input value={e.endDate || ""} disabled={e.current} onChange={(ev) => set(i, "endDate", ev.target.value)} placeholder="Present" /></Field>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Checkbox id={`cur-${i}`} checked={!!e.current} onCheckedChange={(v) => set(i, "current", !!v)} />
              <Label htmlFor={`cur-${i}`} className="text-sm">I currently work here</Label>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-sm">Bullet points</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1 border-blue-200 text-blue-700 bg-blue-50/50 hover:bg-blue-50 hover:text-blue-800"
                  onClick={() => setAiJobIdx(i)}
                  data-testid={`ai-bullets-open-${i}`}
                >
                  <Sparkles className="w-3 h-3" /> Ask AI for bullets
                </Button>
              </div>
              <div className="space-y-2">
                {(e.bullets || []).map((b, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <div className="flex-1">
                      <Textarea rows={2} value={b} onChange={(ev) => setBullet(i, j, ev.target.value)} placeholder="Led redesign of onboarding, increasing activation 28%…" data-testid={`exp-bullet-${i}-${j}`} />
                      <WordCount text={b} suggestedMax={25} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <AiImproveButton context={b} onResult={(t) => setBullet(i, j, t)} small />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeBullet(i, j)} className="h-7 w-7 text-slate-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addBullet(i)} className="h-8" data-testid={`add-bullet-${i}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add bullet
                </Button>
              </div>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addJob} className="w-full h-11 border-dashed" data-testid="add-job-btn">
          <Plus className="w-4 h-4 mr-2" /> Add job
        </Button>
      </div>
      <AIPromptDialog
        open={aiJobIdx !== null}
        onOpenChange={(v) => { if (!v) setAiJobIdx(null); }}
        title="Tell me about this job"
        description="Answer what you can. I'll write bullets using only your answers — no made-up details."
        questions={BULLETS_QUESTIONS}
        action="generate_bullets"
        role={aiJobIdx !== null ? (exp[aiJobIdx]?.role || "") : ""}
        onResult={(t) => {
          if (aiJobIdx === null) return;
          const lines = t.split(/\n+/).map((l) => l.replace(/^\s*\d+[.)]?\s*/, "").trim()).filter(Boolean);
          if (lines.length) set(aiJobIdx, "bullets", lines);
          setAiJobIdx(null);
        }}
      />
    </div>
  );
}

export function EducationStep({ resume, update }) {
  const edu = resume.education || [];
  const set = (i, k, v) => {
    const copy = [...edu]; copy[i] = { ...copy[i], [k]: v }; update({ ...resume, education: copy });
  };
  const add = () => update({ ...resume, education: [...edu, { degree: "", school: "", startDate: "", endDate: "", details: "" }] });
  const remove = (i) => update({ ...resume, education: edu.filter((_, idx) => idx !== i) });

  return (
    <div>
      <StepHeader title="Education" desc="Degrees, certifications, bootcamps — anything relevant." />
      <div className="space-y-4">
        {edu.map((e, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-700">Education #{i + 1}</div>
              <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-red-600 h-8" data-testid={`remove-edu-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Degree"><Input value={e.degree || ""} onChange={(ev) => set(i, "degree", ev.target.value)} placeholder="B.S. Computer Science" data-testid={`edu-degree-${i}`} /></Field>
              <Field label="School"><Input value={e.school || ""} onChange={(ev) => set(i, "school", ev.target.value)} placeholder="Stanford University" /></Field>
              <Field label="Start"><Input value={e.startDate || ""} onChange={(ev) => set(i, "startDate", ev.target.value)} placeholder="2018" /></Field>
              <Field label="End"><Input value={e.endDate || ""} onChange={(ev) => set(i, "endDate", ev.target.value)} placeholder="2022" /></Field>
            </div>
            <div className="mt-3">
              <Field label="Details (optional)">
                <Textarea rows={2} value={e.details || ""} onChange={(ev) => set(i, "details", ev.target.value)} placeholder="GPA, honors, relevant coursework…" />
              </Field>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={add} className="w-full h-11 border-dashed" data-testid="add-edu-btn">
          <Plus className="w-4 h-4 mr-2" /> Add education
        </Button>
      </div>
    </div>
  );
}

export function SkillsStep({ resume, update }) {
  const [input, setInput] = useState("");
  const skills = resume.skills || [];
  const addSkill = (s) => {
    const v = s.trim(); if (!v) return;
    if (skills.includes(v)) return;
    update({ ...resume, skills: [...skills, v] });
    setInput("");
  };
  const remove = (s) => update({ ...resume, skills: skills.filter((x) => x !== s) });

  return (
    <div>
      <StepHeader title="Skills" desc="Add tools, languages, frameworks. Press Enter to add." />
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(input); } }}
          placeholder="Figma, Python, Leadership…"
          data-testid="skills-input"
        />
        <Button type="button" onClick={() => addSkill(input)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-skill-btn">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="skills-list">
        {skills.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 transition pl-3 pr-2 py-1.5 rounded-md text-sm">
            {s}
            <button onClick={() => remove(s)} className="text-slate-400 hover:text-red-600">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
        {skills.length === 0 && <p className="text-sm text-slate-500">No skills added yet.</p>}
      </div>
    </div>
  );
}

export function ProjectsStep({ resume, update }) {
  const projects = resume.projects || [];
  const set = (i, k, v) => {
    const copy = [...projects]; copy[i] = { ...copy[i], [k]: v }; update({ ...resume, projects: copy });
  };
  const add = () => update({ ...resume, projects: [...projects, { name: "", description: "", link: "" }] });
  const remove = (i) => update({ ...resume, projects: projects.filter((_, idx) => idx !== i) });

  return (
    <div>
      <StepHeader title="Projects (optional)" desc="Side projects, open source, or significant initiatives." />
      <div className="space-y-4">
        {projects.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-700">Project #{i + 1}</div>
              <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-red-600 h-8" data-testid={`remove-proj-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name"><Input value={p.name || ""} onChange={(e) => set(i, "name", e.target.value)} placeholder="ResumeLock" data-testid={`proj-name-${i}`} /></Field>
              <Field label="Link"><Input value={p.link || ""} onChange={(e) => set(i, "link", e.target.value)} placeholder="resumelock.app" /></Field>
            </div>
            <div className="mt-3">
              <Field label="Description">
                <Textarea rows={3} value={p.description || ""} onChange={(e) => set(i, "description", e.target.value)} placeholder="A short summary of what it is and your impact." />
                <WordCount text={p.description || ""} suggestedMax={40} />
              </Field>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={add} className="w-full h-11 border-dashed" data-testid="add-proj-btn">
          <Plus className="w-4 h-4 mr-2" /> Add project
        </Button>
      </div>
    </div>
  );
}

export function ReferencesStep({ resume, update }) {
  const refs = resume.references || [];
  const set = (i, k, v) => {
    const copy = [...refs];
    copy[i] = { ...copy[i], [k]: v };
    update({ ...resume, references: copy });
  };
  const add = (type) => update({
    ...resume,
    references: [...refs, { name: "", title: "", email: "", phone: "", type, note: "" }],
  });
  const remove = (i) => update({ ...resume, references: refs.filter((_, idx) => idx !== i) });

  const professionalCount = refs.filter((r) => r.type === "professional").length;
  const personalCount = refs.filter((r) => r.type === "personal").length;
  const showOnResume = !!resume.showReferences;

  return (
    <div>
      <StepHeader
        title="References"
        desc="Add people who can speak about your work. We recommend 3 or more professional references. You can also add personal references."
      >
        <span className="text-xs text-emerald-700 inline-flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
          <Lock className="w-3 h-3" /> Encrypted on save
        </span>
      </StepHeader>

      <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 flex items-start gap-3" data-testid="show-references-toggle-row">
        <Checkbox
          id="show-refs"
          checked={showOnResume}
          onCheckedChange={(v) => update({ ...resume, showReferences: !!v })}
          data-testid="show-references-checkbox"
        />
        <div className="text-sm">
          <Label htmlFor="show-refs" className="font-medium text-slate-900 cursor-pointer">
            Include references on the resume
          </Label>
          <div className="text-slate-500 mt-0.5">
            Off by default — most jobs say <span className="italic">references available on request</span>. You can still download a separate <span className="font-medium">References sheet</span> from the download menu and share it when asked.
          </div>
        </div>
      </div>

      {professionalCount < 3 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">Tip:</span> Most jobs ask for at least <span className="font-medium">3 professional references</span>.
          You have {professionalCount} so far.
        </div>
      )}

      <div className="space-y-4">
        {refs.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="references-empty">
            No references yet. Add your first one below.
          </div>
        )}
        {refs.map((r, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-white" data-testid={`reference-card-${i}`}>
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Reference #{i + 1}</span>
                <span
                  className={`text-[10px] uppercase tracking-wider font-mono-display px-2 py-0.5 rounded ${r.type === "professional" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}
                >
                  {r.type === "professional" ? "Professional" : "Personal"}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-red-600 hover:text-red-700 h-8" data-testid={`remove-ref-${i}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full name *">
                <Input value={r.name || ""} onChange={(e) => set(i, "name", e.target.value)} placeholder="Pat Williams" data-testid={`ref-name-${i}`} />
              </Field>
              <Field label="Title / role *">
                <Input value={r.title || ""} onChange={(e) => set(i, "title", e.target.value)} placeholder="Warehouse Manager" data-testid={`ref-title-${i}`} />
              </Field>
              <Field label="Email *">
                <Input type="email" value={r.email || ""} onChange={(e) => set(i, "email", e.target.value)} placeholder="pat.williams@acme.com" data-testid={`ref-email-${i}`} />
              </Field>
              <Field label="Phone (optional)">
                <Input type="tel" value={r.phone || ""} onChange={(e) => set(i, "phone", e.target.value)} placeholder="(303) 555-0100 — cell or work" data-testid={`ref-phone-${i}`} />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Why this person? (helps a hiring manager understand the connection)">
                <Textarea
                  rows={2}
                  value={r.note || ""}
                  onChange={(e) => set(i, "note", e.target.value)}
                  placeholder="Worked together on the warehouse safety project; she supervised me for 2 years."
                  data-testid={`ref-note-${i}`}
                />
              </Field>
            </div>
          </div>
        ))}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => add("professional")}
            className="h-11 border-dashed"
            data-testid="add-professional-ref-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add professional reference
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => add("personal")}
            className="h-11 border-dashed"
            data-testid="add-personal-ref-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add personal reference
          </Button>
        </div>
        <div className="text-xs text-slate-500 mt-2">
          You have <span className="font-medium text-slate-700">{professionalCount}</span> professional and{" "}
          <span className="font-medium text-slate-700">{personalCount}</span> personal references.
        </div>
      </div>
    </div>
  );
}

export function ReviewStep({ resume }) {
  const pi = resume.personalInfo || {};
  const sections = [
    { t: "Personal info", v: [pi.fullName, pi.email].filter(Boolean).join(" • ") || "—" },
    { t: "Summary", v: resume.summary ? `${resume.summary.slice(0, 80)}${resume.summary.length > 80 ? "…" : ""}` : "—" },
    { t: "Experience", v: `${(resume.experience || []).length} role(s)` },
    { t: "Education", v: `${(resume.education || []).length} entry(ies)` },
    { t: "Skills", v: `${(resume.skills || []).length} skill(s)` },
    { t: "Projects", v: `${(resume.projects || []).length} project(s)` },
    { t: "References", v: `${(resume.references || []).length} reference(s)` },
  ];
  return (
    <div>
      <StepHeader title="Review & download" desc="Final check before you export. You can come back and edit anytime." />
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
        {sections.map((s) => (
          <div key={s.t} className="flex justify-between px-4 py-3 text-sm">
            <span className="text-slate-500">{s.t}</span>
            <span className="font-medium text-slate-900 text-right max-w-[60%] truncate">{s.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepHeader({ title, desc, children }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">{desc}</p>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
